import { LitElement, html, property, customElement, TemplateResult, internalProperty } from "lit-element";
import { CategoryInfo } from "../models/category-info";

@customElement("teaser-filter")
export class TeaserFilter extends LitElement {
  // correlation id to the teaser widget
  @property({ type: String }) public teaserWidgetCorrelationId!: string;
  // Must be 1, 2 or 3. Specifies if this filter dropdown is the first, second or third one.
  @property({ type: Number }) public categoryFilterIndex!: number;
  // Main category
  @property({ type: Object }) public mainCategory!: CategoryInfo;
  // Sub categories of main category
  @property({ type: Array }) public subCategories!: CategoryInfo[];

  @internalProperty()
  private selectedCategoryCodeName: string | null = null;

  private get urlParameterName(): string {
    return `category-${this.categoryFilterIndex}-${this.teaserWidgetCorrelationId}`;
  }

  private get selectedDisplayName(): string {
    return (
      this.subCategories.find((cat) => cat.codeName === this.selectedCategoryCodeName)?.displayName ??
      this.mainCategory.displayName
    );
  }

  public firstUpdated(): void {
    const urlParameters = new URLSearchParams(window.location.search);
    let initialCodeName = urlParameters.get(this.urlParameterName);
    if (initialCodeName == null || !this.subCategories.some((category) => category.codeName === initialCodeName)) {
      initialCodeName = this.mainCategory.codeName;
    }
    this.selectedCategoryCodeName = initialCodeName;
  }

  // disable shadow DOM
  protected createRenderRoot(): this {
    return this;
  }

  protected render(): TemplateResult | "" {
    if (
      this.mainCategory == null ||
      this.subCategories == null ||
      this.categoryFilterIndex < 1 ||
      this.categoryFilterIndex > 3
    ) {
      console.error("Teaser filter parameters are invalid.");
      return "";
    }

    const dropdownId = `dropdownMenuButton-${this.teaserWidgetCorrelationId}-${this.categoryFilterIndex}`;

    return html`<div class="dropdown">
      <button
        class="btn dropdown-toggle w-100"
        type="button"
        id="${dropdownId}"
        data-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        ${this.selectedDisplayName}
      </button>
      <div class="dropdown-menu" aria-labelledby="${dropdownId}">
        <button class="dropdown-item" type="button" @click="${this.onDropdownItemClick}" data-codename="">
          ${this.mainCategory.displayName}
        </button>
        <div class="dropdown-divider"></div>
        ${this.subCategories.map(
          (item) =>
            html`<button
              class="dropdown-item"
              type="button"
              @click="${this.onDropdownItemClick}"
              data-codename="${item.codeName}"
            >
              ${item.displayName}
            </button>`
        )}
      </div>
    </div>`;
  }

  private onDropdownItemClick(event: Event): void {
    const dropdownItemButton = event.target as HTMLButtonElement;
    if (!dropdownItemButton || dropdownItemButton.dataset.codename === undefined) {
      return;
    }

    const codeName = dropdownItemButton.dataset.codename;
    this.selectedCategoryCodeName = codeName;
    const urlParameters = new URLSearchParams(window.location.search);
    urlParameters.set(this.urlParameterName, codeName);
    history.pushState(null, "", `${window.location.pathname}?${urlParameters.toString()}`);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }
}
